import { useState, useCallback, useEffect } from 'react';
import { Ban, Eye, EyeOff, Loader2, Plus, RefreshCw, Settings as SettingsIcon, ShieldOff, Trash2 } from 'lucide-react';
import { useAccountStore } from '../../stores/accountStore';
import { usePolyAccountStore } from '../../stores/polyAccountStore';
import { isValidEvmAddress, isValidEvmPrivateKey } from '../../lib/polymarket/client';
import { apiFetch, useAuthStore } from '../../stores/authStore';
import { ConfirmModal } from '../../components/atoms/Modal';
import {
  CredentialHealthLines,
  CredentialStateBadge,
  type CredentialLifecycle,
  getCredentialState,
  isCredentialEnabled,
} from '../../components/credential/CredentialLifecycle';
import {
  useDisplaySettings,
  type DashboardMarketType,
  type DefaultExpiryMode,
  type PositionViewMode,
  type SurfaceViewMode,
  type DashboardSortKey,
  type DashboardSortDir,
  type PositionsOrderSortKey,
  type PositionsOrderSortDir,
} from '../../stores/displaySettings';
import { SPORT_TABS, type Sport, type PriceMode } from '../ValueDashboard/types';
import { SoundSettingsPanel } from './SoundSettingsPanel';

interface StoredCredential extends CredentialLifecycle {
  id: string;
  label: string;
  accessKey: string;
  venue: string;
  environment: string;
  createdAt: string;
}

export function Settings() {
  const status = useAccountStore((s) => s.status);
  const error = useAccountStore((s) => s.error);
  const balance = useAccountStore((s) => s.balance);
  const keyMode = useAccountStore((s) => s.keyMode);
  const connect = useAccountStore((s) => s.connect);
  const disconnect = useAccountStore((s) => s.disconnect);
  const checkCredentials = useAccountStore((s) => s.checkCredentials);

  // Personal-key Polymarket trading is disabled for trader-role users in
  // this app — see PolyPersonalKeyClearedBanner + AuthedShell central mount.
  // We hide the form here so traders can't see the disabled control either.
  const role = useAuthStore((s) => s.user?.role);
  const personalPolyHidden = role === 'trader';

  const [credentials, setCredentials] = useState<StoredCredential[]>([]);
  const [loadingCreds, setLoadingCreds] = useState(true);
  const [credsError, setCredsError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [actingCredentialId, setActingCredentialId] = useState<string | null>(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState<StoredCredential | null>(null);

  const enabledCredentialCount = credentials.filter(isCredentialEnabled).length;

  const fetchCredentials = useCallback(async () => {
    setLoadingCreds(true);
    setCredsError(null);
    try {
      const res = await apiFetch('/api/trader/credentials');
      if (res.ok) {
        const data = await res.json();
        setCredentials(data.credentials ?? []);
      } else {
        setCredsError(`Failed to load keys (${res.status}). Try refreshing.`);
      }
    } catch {
      setCredsError('Could not reach the server. Try refreshing.');
    }
    setLoadingCreds(false);
  }, []);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const handleAddKey = useCallback(async (key: {
    label: string;
    accessKey: string;
    privateKeyPem: string;
    environment: 'prod' | 'demo';
  }) => {
    setActionError(null);
    const res = await apiFetch('/api/trader/credentials', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(key),
    });
    const data = await res.json().catch(() => ({})) as {
      credential?: StoredCredential;
      error?: string;
    };
    if (!res.ok || !data.credential) {
      setActionError(data.error ?? `Failed to save key (${res.status}).`);
      return false;
    }
    setCredentials((prev) => [...prev, data.credential!]);
    setShowAddForm(false);
    await checkCredentials();
    if (status === 'disconnected' || status === 'error') {
      void connect();
    }
    return true;
  }, [status, connect, checkCredentials]);

  const handlePatchCredential = useCallback(async (
    credential: StoredCredential,
    body: { disabled?: boolean; release?: boolean },
  ) => {
    setActionError(null);
    setActingCredentialId(credential.id);
    const res = await apiFetch(`/api/trader/credentials/${credential.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({})) as {
      credential?: StoredCredential;
      error?: string;
    };
    if (!res.ok || !data.credential) {
      setActionError(data.error ?? `Failed to update key (${res.status}).`);
      setActingCredentialId(null);
      return;
    }
    const nextCredentials = credentials.map((c) => (c.id === credential.id ? data.credential! : c));
    setCredentials(nextCredentials);
    if (nextCredentials.length === 0) disconnect();
    await checkCredentials();
    if (nextCredentials.filter(isCredentialEnabled).length === 0) disconnect();
    setActingCredentialId(null);
  }, [credentials, disconnect, checkCredentials]);

  const handleHardDeleteKey = useCallback(async () => {
    if (!hardDeleteTarget) return;
    const target = hardDeleteTarget;
    setHardDeleteTarget(null);
    setActionError(null);
    setActingCredentialId(target.id);
    const res = await apiFetch(
      `/api/trader/credentials/${target.id}?hard=true&confirm=delete`,
      { method: 'DELETE' },
    );
    const data = await res.json().catch(() => ({})) as { error?: string };
    if (!res.ok) {
      setActionError(data.error ?? `Failed to delete key (${res.status}).`);
      setActingCredentialId(null);
      return;
    }
    const nextCredentials = credentials.filter((c) => c.id !== target.id);
    setCredentials(nextCredentials);
    if (nextCredentials.filter(isCredentialEnabled).length === 0) disconnect();
    await checkCredentials();
    setActingCredentialId(null);
  }, [credentials, disconnect, checkCredentials, hardDeleteTarget]);

  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <SettingsIcon size={24} className="text-muted-foreground" />
          <h1 className="text-xl font-semibold">Settings</h1>
        </div>

        {/* Kalshi Account Card */}
        <div className="bg-card border-border rounded-lg border p-5">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-base font-medium">Kalshi Credentials</h2>
            <StatusBadge status={status} balance={balance} keyMode={keyMode} />
          </div>

          {/* Stored Keys */}
          <div className="mb-4">
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-muted-foreground text-xs font-medium">
                Stored Keys ({credentials.length === 0 ? 'none' : `${enabledCredentialCount}/${credentials.length} enabled`})
              </label>
              <button onClick={fetchCredentials} title="Refresh"
                className="text-muted-foreground hover:text-foreground rounded p-1">
                <RefreshCw size={12} />
              </button>
            </div>

            {loadingCreds ? (
              <div className="flex justify-center py-4">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : credsError ? (
              <div className="flex items-center justify-between rounded-md bg-red-500/10 px-3 py-2">
                <p className="text-xs text-red-500 dark:text-red-400">{credsError}</p>
                <button onClick={fetchCredentials}
                  className="ml-2 flex items-center gap-1 rounded px-2 py-1 text-xs text-red-500 hover:bg-red-500/10 dark:text-red-400">
                  <RefreshCw size={10} /> Retry
                </button>
              </div>
            ) : credentials.length > 0 ? (
              <div className="space-y-1.5">
                {credentials.map((cred) => (
                  <CredentialRow
                    key={cred.id}
                    credential={cred}
                    acting={actingCredentialId === cred.id}
                    onRelease={() => handlePatchCredential(cred, { release: true })}
                    onToggleDisabled={() => handlePatchCredential(
                      cred,
                      { disabled: isCredentialEnabled(cred) },
                    )}
                    onHardDelete={() => setHardDeleteTarget(cred)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-xs">No keys stored. Add a key to connect.</p>
            )}
          </div>

          {/* Add Key */}
          {showAddForm ? (
            <AddKeyForm onAdd={handleAddKey} onCancel={() => setShowAddForm(false)} />
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-blue-500 transition-colors hover:bg-blue-500/10 dark:text-blue-400"
            >
              <Plus size={14} />
              Add Key
            </button>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500 dark:text-red-400">
              {error}
            </div>
          )}
          {actionError && (
            <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500 dark:text-red-400">
              {actionError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="border-border mt-5 flex items-center gap-3 border-t pt-4">
            {!isConnected && credentials.length > 0 && (
              <button
                onClick={() => connect()}
                disabled={isConnecting}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600"
              >
                {isConnecting && <Loader2 size={14} className="animate-spin" />}
                {isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            )}
            {isConnected && (
              <button
                onClick={disconnect}
                className="bg-secondary text-foreground hover:bg-accent rounded-md px-4 py-2 text-sm font-medium transition-colors"
              >
                Disconnect
              </button>
            )}
          </div>
        </div>

        {/* Polymarket Account Card — hidden for trader-role users (personal-
            key Polymarket disabled by the org). Admin/manager paths intact. */}
        {personalPolyHidden ? (
          <div
            data-testid="personal-poly-disabled-notice"
            className="bg-card border-border flex items-start gap-3 rounded-lg border px-4 py-3 text-xs text-muted-foreground"
          >
            <ShieldOff size={14} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-foreground font-medium">Personal Polymarket trading not available</p>
              <p className="mt-0.5">
                Trader accounts use the org&apos;s shared Polymarket credential. Ask an
                admin or manager to configure org-level Polymarket keys if your
                workflow needs it.
              </p>
            </div>
          </div>
        ) : (
          <PolymarketAccountCard />
        )}

        {/* User Preferences — local-only defaults applied at mount time */}
        <PreferencesPanel />

        {/* Sound Notifications */}
        <SoundSettingsPanel />
      </div>
      <ConfirmModal
        isOpen={!!hardDeleteTarget}
        onClose={() => setHardDeleteTarget(null)}
        onConfirm={handleHardDeleteKey}
        title="Permanently Delete Key"
        message={`Permanently delete "${hardDeleteTarget?.label}"? This removes encrypted key material and lifecycle history. Disable is safer unless this key should never be recoverable.`}
        confirmLabel="Delete Permanently"
        variant="danger"
      />
    </div>
  );
}

// ── Status Badge ────────────────────────────────────────────────

function StatusBadge({
  status,
  balance,
  keyMode,
}: {
  status: string;
  balance: number | null;
  keyMode?: 'org' | 'user' | null;
}) {
  const dot =
    status === 'connected' ? 'bg-green-500' :
    status === 'connecting' ? 'bg-yellow-500 animate-pulse' :
    status === 'error' ? 'bg-red-500' :
    'bg-muted-foreground/40';

  const baseLabel =
    status === 'connected' ? 'Connected' :
    status === 'connecting' ? 'Connecting...' :
    status === 'error' ? 'Error' :
    'Disconnected';

  // Show the resolved key pool whenever we know it (connected or otherwise) so
  // a user can tell at a glance whether the relay is routing them through org
  // or personal keys — surfaced specifically because org-key fallback bugs
  // were invisible from this badge.
  const modeSuffix = keyMode ? ` (${keyMode === 'org' ? 'Org' : 'Personal'})` : '';
  const label = `${baseLabel}${modeSuffix}`;

  return (
    <div className="flex items-center gap-3 text-xs">
      {status === 'connected' && balance !== null && (
        <span className="text-muted-foreground">
          Balance: <span className="text-foreground font-medium">${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </span>
      )}
      <div className="flex items-center gap-1.5">
        <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
        <span className="text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

// ── Credential Row ──────────────────────────────────────────────

function CredentialRow({
  credential,
  acting,
  onRelease,
  onToggleDisabled,
  onHardDelete,
}: {
  credential: StoredCredential;
  acting: boolean;
  onRelease: () => void;
  onToggleDisabled: () => void;
  onHardDelete: () => void;
}) {
  const state = getCredentialState(credential);
  const disabled = state.kind === 'disabled';
  const canRelease =
    !disabled &&
    (!!credential.quarantinedUntil ||
      Number(credential.consecutiveAuthFailures ?? 0) > 0 ||
      !!credential.lastErrorAt);
  const truncated =
    credential.accessKey.length > 10
      ? `${credential.accessKey.slice(0, 6)}...${credential.accessKey.slice(-4)}`
      : credential.accessKey;

  return (
    <div className={`bg-muted/50 rounded-md px-3 py-2 ${disabled ? 'opacity-75' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-foreground text-xs font-medium">{credential.label}</span>
            <CredentialStateBadge credential={credential} />
            <span className="bg-secondary text-muted-foreground rounded px-1.5 py-0.5 text-[10px]">
              {credential.environment}
            </span>
          </div>
          <span className="text-muted-foreground block font-mono text-xs">{truncated}</span>
          <CredentialHealthLines credential={credential} />
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {canRelease && (
            <button
              onClick={onRelease}
              disabled={acting}
              title="Release quarantine and reset auth failure count"
              className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-blue-500/10 hover:text-blue-500 disabled:opacity-50"
            >
              <RefreshCw size={12} className={acting ? 'animate-spin' : ''} />
              Release
            </button>
          )}
          <button
            onClick={onToggleDisabled}
            disabled={acting}
            title={disabled ? 'Enable key' : 'Disable key'}
            className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Ban size={12} />
            {disabled ? 'Enable' : 'Disable'}
          </button>
          {disabled && (
            <button
              onClick={onHardDelete}
              disabled={acting}
              title="Permanently delete key"
              className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-red-500/10 hover:text-red-500 disabled:opacity-50"
            >
              <Trash2 size={12} />
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Add Key Form ────────────────────────────────────────────────

function AddKeyForm({
  onAdd,
  onCancel,
}: {
  onAdd: (key: {
    label: string;
    accessKey: string;
    privateKeyPem: string;
    environment: 'prod' | 'demo';
  }) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [privateKeyPem, setPrivateKeyPem] = useState('');
  const [environment, setEnvironment] = useState<'prod' | 'demo'>('prod');
  const [showPem, setShowPem] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setLocalError(null);

    if (!accessKey.trim()) { setLocalError('Access key is required.'); return; }
    if (!privateKeyPem.trim()) { setLocalError('Private key PEM is required.'); return; }
    if (!privateKeyPem.includes('BEGIN PRIVATE KEY') && !privateKeyPem.includes('BEGIN RSA PRIVATE KEY')) {
      setLocalError('PEM must start with -----BEGIN PRIVATE KEY----- or -----BEGIN RSA PRIVATE KEY-----');
      return;
    }

    setSubmitting(true);
    try {
      const ok = await onAdd({
        label: label.trim() || `Key ${Date.now().toString(36)}`,
        accessKey: accessKey.trim(),
        privateKeyPem: privateKeyPem.trim(),
        environment,
      });
      if (ok) {
        setLabel('');
        setAccessKey('');
        setPrivateKeyPem('');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-muted/50 border-border rounded-md border p-3">
      <div className="space-y-3">
        {/* Environment */}
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">Environment</label>
          <div className="flex gap-2">
            {(['prod', 'demo'] as const).map((env) => (
              <button key={env} onClick={() => setEnvironment(env)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  environment === env
                    ? 'bg-blue-500/20 text-blue-600 ring-1 ring-blue-500/40 dark:text-blue-400'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}>
                {env === 'prod' ? 'Production' : 'Demo'}
              </button>
            ))}
          </div>
        </div>

        {/* Label */}
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">Label (optional)</label>
          <input type="text" value={label}
            onChange={(e) => { setLabel(e.target.value); setLocalError(null); }}
            placeholder="e.g., Trading Key 1"
            className="bg-card border-border text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none" />
        </div>

        {/* Access Key */}
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">Access Key</label>
          <input type="password" value={accessKey}
            onChange={(e) => { setAccessKey(e.target.value); setLocalError(null); }}
            placeholder="Your Kalshi access key ID"
            className="bg-card border-border text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none" />
        </div>

        {/* Private Key PEM */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-muted-foreground text-xs">Private Key (PKCS#8 PEM)</label>
            <button onClick={() => setShowPem((v) => !v)} className="text-muted-foreground hover:text-foreground">
              {showPem ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <textarea value={privateKeyPem}
            onChange={(e) => { setPrivateKeyPem(e.target.value); setLocalError(null); }}
            placeholder="-----BEGIN PRIVATE KEY-----"
            rows={4}
            className="bg-card border-border text-foreground placeholder-muted-foreground w-full resize-y rounded-md border px-3 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none"
            style={!showPem && privateKeyPem ? { WebkitTextSecurity: 'disc' } as React.CSSProperties : undefined} />
        </div>

        {localError && <div className="text-xs text-red-500 dark:text-red-400">{localError}</div>}

        <div className="flex items-center gap-2">
          <button onClick={() => void handleSubmit()} disabled={submitting}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50">
            {submitting ? 'Saving...' : 'Add Key'}
          </button>
          <button onClick={onCancel} disabled={submitting}
            className="text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-xs transition-colors disabled:opacity-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Polymarket Account Card ────────────────────────────────────

const POLY_SIG_TYPE_DESCRIPTIONS: Record<0 | 1 | 2 | 3, string> = {
  0: 'Self-custodied EOA (MetaMask, programmatic). Signer and funder are the same address — no separate funder needed.',
  1: 'Existing Magic Link email-login users. The key signs orders; a Polymarket proxy contract holds your collateral.',
  2: 'Existing browser-wallet users with a Polymarket Safe. The key signs orders; a Gnosis Safe holds your collateral.',
  3: 'New polymarket.com onboardees (POLY_1271 / Deposit Wallet). The key signs ERC-7739 wrapped orders; a CREATE2-derived deposit wallet holds your collateral. Paste the wallet address shown on your Polymarket profile as the funder.',
};

function PolymarketAccountCard() {
  const polyStatus = usePolyAccountStore((s) => s.status);
  const polyError = usePolyAccountStore((s) => s.error);
  const polyConfig = usePolyAccountStore((s) => s.config);
  const polyFunderBalance = usePolyAccountStore((s) => s.funderBalance);
  const polyConnect = usePolyAccountStore((s) => s.connect);
  const polyDisconnect = usePolyAccountStore((s) => s.disconnect);
  const polyRemoveCredentials = usePolyAccountStore((s) => s.removeCredentials);

  const [privateKey, setPrivateKey] = useState('');
  // Default to Gnosis Safe, the common existing browser-wallet setup this
  // settings card supports.
  const [signatureType, setSignatureType] = useState<0 | 1 | 2 | 3>(2);
  const [funder, setFunder] = useState('');
  const [showKey, setShowKey] = useState(false);

  const isConnected = polyStatus === 'connected';
  const isConnecting = polyStatus === 'connecting';

  const pkTrim = privateKey.trim();
  const funderTrim = funder.trim();
  const pkFormatError = pkTrim && !isValidEvmPrivateKey(pkTrim)
    ? 'Must be a valid 0x + 64-hex EVM private key.'
    : null;
  const showFunderMissing = signatureType !== 0 && !!pkTrim && !funderTrim;
  const funderFormatError =
    signatureType !== 0 && funderTrim && !isValidEvmAddress(funderTrim)
      ? 'Must be a valid 0x address (40 hex chars).'
      : null;
  const funderError = showFunderMissing
    ? 'Funder address is required for Magic, Gnosis, and Deposit Wallet.'
    : funderFormatError;
  const funderMissing = signatureType !== 0 && !funderTrim;
  const canSubmit =
    !!pkTrim && !pkFormatError && !funderError && !funderMissing && !isConnecting;

  const handleConnect = useCallback(async () => {
    if (!privateKey.trim()) return;
    await polyConnect({
      privateKey: privateKey.trim(),
      signatureType,
      funder: funder.trim() || undefined,
    });
  }, [privateKey, signatureType, funder, polyConnect]);

  const handleDisconnect = useCallback(() => {
    polyDisconnect();
    setPrivateKey('');
    setFunder('');
  }, [polyDisconnect]);

  const handleRemoveCredentials = useCallback(() => {
    polyRemoveCredentials();
    setPrivateKey('');
    setFunder('');
  }, [polyRemoveCredentials]);

  return (
    <div className="bg-card border-border rounded-lg border p-5">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-base font-medium">Polymarket Account</h2>
        <StatusBadge status={polyStatus} balance={null} />
      </div>

      {!isConnected && (
        <>
          <div className="mb-4">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-muted-foreground text-xs font-medium">Private Key</label>
              <button onClick={() => setShowKey((v) => !v)} className="text-muted-foreground hover:text-foreground">
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <input type={showKey ? 'text' : 'password'} value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)} placeholder="0x..."
              aria-invalid={!!pkFormatError}
              className={`bg-card text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-1.5 font-mono text-xs focus:outline-none ${
                pkFormatError ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-blue-500'
              }`} />
            {pkFormatError && (
              <div className="mt-1 text-xs text-red-500 dark:text-red-400">{pkFormatError}</div>
            )}
          </div>

          <div className="mb-4">
            <label className="text-muted-foreground mb-1.5 block text-xs font-medium">Signature Type</label>
            <div className="flex gap-2">
              {([0, 1, 2, 3] as const).map((t) => {
                const lbl =
                  t === 0 ? 'EOA' : t === 1 ? 'Magic' : t === 2 ? 'Gnosis' : 'Deposit';
                return (
                  <button key={t} onClick={() => setSignatureType(t)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      signatureType === t
                        ? 'bg-blue-500/20 text-blue-600 ring-1 ring-blue-500/40 dark:text-blue-400'
                        : 'bg-secondary text-muted-foreground hover:text-foreground'
                    }`}>
                    {lbl}
                  </button>
                );
              })}
            </div>
            <p className="text-muted-foreground mt-2 text-xs leading-snug">
              {POLY_SIG_TYPE_DESCRIPTIONS[signatureType]}
            </p>
          </div>

          {signatureType !== 0 && (
            <div className="mb-4">
              <label className="text-muted-foreground mb-1 block text-xs font-medium">
                Funder Address <span className="text-red-500">*</span>
              </label>
              <input type="text" value={funder} onChange={(e) => setFunder(e.target.value)}
                placeholder="0x..."
                aria-invalid={!!funderError}
                className={`bg-card text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-1.5 font-mono text-xs focus:outline-none ${
                  funderError ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-blue-500'
                }`} />
              {funderError ? (
                <div className="mt-1 text-xs text-red-500 dark:text-red-400">{funderError}</div>
              ) : (
                <p className="text-muted-foreground mt-1 text-xs leading-snug">
                  The wallet address shown on your Polymarket profile — the proxy/Safe that holds your collateral, not the address derived from your signer key.
                </p>
              )}
            </div>
          )}
        </>
      )}

      {isConnected && polyConfig && (
        <div className="mb-4 space-y-3">
          <div>
            <label className="text-muted-foreground mb-1 block text-xs font-medium">Signer Address</label>
            <div className="bg-muted/50 text-foreground break-all rounded-md px-3 py-2 font-mono text-xs">
              {polyConfig.signerAddress ?? '—'}
            </div>
          </div>
          {polyConfig.signatureType !== 0 && polyConfig.funder && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs font-medium">Funder Address</label>
              <div className="bg-muted/50 text-foreground break-all rounded-md px-3 py-2 font-mono text-xs">
                {polyConfig.funder}
              </div>
            </div>
          )}
          {polyFunderBalance && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs font-medium">Collateral USDC</label>
              <div className="bg-muted/50 text-foreground rounded-md px-3 py-2 font-mono text-xs">
                ${polyFunderBalance.collateralUsd.toFixed(2)}
                <span className="text-muted-foreground ml-2">
                  (allowance ${polyFunderBalance.collateralAllowanceUsd.toFixed(2)})
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {polyError && (
        <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500 dark:text-red-400">{polyError}</div>
      )}

      <div className="border-border mt-5 flex items-center gap-3 border-t pt-4">
        {!isConnected ? (
          <button onClick={handleConnect} disabled={!canSubmit}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600">
            {isConnecting && <Loader2 size={14} className="animate-spin" />}
            {isConnecting ? 'Connecting...' : 'Connect'}
          </button>
        ) : (
          <>
            <button onClick={handleDisconnect}
              className="bg-secondary text-foreground hover:bg-accent rounded-md px-4 py-2 text-sm font-medium transition-colors">
              Disconnect
            </button>
            <button onClick={handleRemoveCredentials}
              className="rounded-md px-3 py-2 text-xs text-red-500 transition-colors hover:bg-red-500/10 dark:text-red-400">
              Remove Credentials
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── User Preferences (local-only, persisted to localStorage) ─────

const MARKET_TYPE_CHOICES: { id: DashboardMarketType; label: string }[] = [
  { id: 'moneyline', label: 'Moneyline' },
  { id: 'spread-total', label: 'Spread/Total' },
  { id: 'series', label: 'Futures' },
];

const PRICE_MODE_CHOICES: { id: PriceMode; label: string; hint: string }[] = [
  { id: 'maker', label: 'Maker', hint: 'Best bid — what someone will pay you' },
  { id: 'taker', label: 'Taker', hint: 'Best ask — what you pay to cross now' },
];

const EXPIRY_MODE_CHOICES: { id: DefaultExpiryMode; label: string; hint: string }[] = [
  { id: 'gts', label: 'Good to Start', hint: 'Auto-cancels at scheduled event start if unfilled' },
  { id: 'gtc', label: 'Good to Close', hint: 'Rests until you cancel or the market settles' },
  { id: 'custom', label: 'Custom', hint: 'Open the ticket on the custom datetime picker' },
];

// Dashboard sort default — `unsorted` is a UI-only sentinel that maps to
// `key: null` in displaySettings; selecting it hides the direction picker
// since asc/desc has no meaning with no applied sort. Keep this list in sync
// with DashboardSortKey + the SortableTh keys in GameTable.tsx.
type DashboardSortKeyChoice = 'unsorted' | DashboardSortKey;
const DASHBOARD_SORT_KEY_CHOICES: { id: DashboardSortKeyChoice; label: string }[] = [
  { id: 'unsorted', label: 'Unsorted' },
  { id: 'date', label: 'Date' },
  { id: 'team', label: 'Team' },
  { id: 'queue', label: 'Queue' },
  { id: 'velocity', label: 'Velocity' },
];
const DASHBOARD_SORT_DIR_CHOICES: { id: DashboardSortDir; label: string }[] = [
  { id: 'asc', label: 'Asc' },
  { id: 'desc', label: 'Desc' },
];

const POSITIONS_ORDER_SORT_KEY_CHOICES: { id: PositionsOrderSortKey; label: string }[] = [
  { id: 'default', label: 'Default' },
  { id: 'size', label: 'Size' },
  { id: 'queue', label: 'Queue' },
  { id: 'velocity', label: 'Velocity' },
];
const POSITIONS_ORDER_SORT_DIR_CHOICES: { id: PositionsOrderSortDir; label: string }[] = [
  { id: 'asc', label: 'Asc' },
  { id: 'desc', label: 'Desc' },
];

function PreferencesPanel() {
  const positionViewMode = useDisplaySettings((s) => s.positionViewMode);
  const orderViewMode = useDisplaySettings((s) => s.orderViewMode);
  const tradeViewMode = useDisplaySettings((s) => s.tradeViewMode);
  const defaultSport = useDisplaySettings((s) => s.defaultSport);
  const defaultMarketType = useDisplaySettings((s) => s.defaultMarketType);
  const defaultPriceMode = useDisplaySettings((s) => s.defaultPriceMode);
  const bookDepthLevels = useDisplaySettings((s) => s.bookDepthLevels);
  const setBookDepthLevels = useDisplaySettings((s) => s.setBookDepthLevels);
  const defaultExpiryMode = useDisplaySettings((s) => s.defaultExpiryMode);
  const defaultContracts = useDisplaySettings((s) => s.defaultContracts);
  const defaultDashboardSort = useDisplaySettings((s) => s.defaultDashboardSort);
  const defaultPositionsOrderSort = useDisplaySettings((s) => s.defaultPositionsOrderSort);
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  const setPositionViewMode = useDisplaySettings((s) => s.setPositionViewMode);
  const setOrderViewMode = useDisplaySettings((s) => s.setOrderViewMode);
  const setTradeViewMode = useDisplaySettings((s) => s.setTradeViewMode);
  const setDefaultSport = useDisplaySettings((s) => s.setDefaultSport);
  const setDefaultMarketType = useDisplaySettings((s) => s.setDefaultMarketType);
  const setDefaultPriceMode = useDisplaySettings((s) => s.setDefaultPriceMode);
  const setDefaultExpiryMode = useDisplaySettings((s) => s.setDefaultExpiryMode);
  const setDefaultContracts = useDisplaySettings((s) => s.setDefaultContracts);
  const setDefaultDashboardSort = useDisplaySettings((s) => s.setDefaultDashboardSort);
  const setDefaultPositionsOrderSort = useDisplaySettings((s) => s.setDefaultPositionsOrderSort);
  const setConfirmOrderCancel = useDisplaySettings((s) => s.setConfirmOrderCancel);
  const pinnedOrderTicket = useDisplaySettings((s) => s.pinnedOrderTicket);
  const setPinnedOrderTicket = useDisplaySettings((s) => s.setPinnedOrderTicket);

  return (
    <div className="bg-card border-border rounded-lg border p-5">
      <div className="mb-5">
        <h2 className="text-base font-medium">Preferences</h2>
        <p className="text-muted-foreground mt-1 text-xs">
          Saved locally to this browser.
        </p>
      </div>

      <div className="space-y-5">
        <PrefSection
          label="Positions view"
          hint="Default layout for the Open Positions section."
        >
          <ChoiceRow
            options={[
              { id: 'compact', label: 'Compact' },
              { id: 'table', label: 'Table' },
              { id: 'card', label: 'Card' },
              { id: 'mini', label: 'Mini' },
            ]}
            value={positionViewMode}
            onChange={(id) => setPositionViewMode(id as PositionViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Open orders view"
          hint="Default layout for the Open Orders section."
        >
          <ChoiceRow
            options={[
              { id: 'card', label: 'Card' },
              { id: 'table', label: 'Table' },
            ]}
            value={orderViewMode}
            onChange={(id) => setOrderViewMode(id as SurfaceViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Trades view"
          hint="Default layout for My Trades."
        >
          <ChoiceRow
            options={[
              { id: 'card', label: 'Card' },
              { id: 'table', label: 'Table' },
            ]}
            value={tradeViewMode}
            onChange={(id) => setTradeViewMode(id as SurfaceViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Default sport"
          hint="Which sport tab the Value Dashboard lands on."
        >
          <ChoiceRow
            options={SPORT_TABS.map((t) => ({ id: t.id, label: t.label }))}
            value={defaultSport}
            onChange={(id) => setDefaultSport(id as Sport)}
          />
        </PrefSection>

        <PrefSection
          label="Default market type"
          hint="Which market-type tab opens first."
        >
          <ChoiceRow
            options={MARKET_TYPE_CHOICES}
            value={defaultMarketType}
            onChange={(id) => setDefaultMarketType(id as DashboardMarketType)}
          />
        </PrefSection>

        <PrefSection
          label="Default price mode"
          hint={
            PRICE_MODE_CHOICES.find((c) => c.id === defaultPriceMode)?.hint ?? ''
          }
        >
          <ChoiceRow
            options={PRICE_MODE_CHOICES}
            value={defaultPriceMode}
            onChange={(id) => setDefaultPriceMode(id as PriceMode)}
          />
        </PrefSection>

        <PrefSection
          label="Order book depth"
          hint="Price levels shown per side in the trade panel's Kalshi/Polymarket books. 8 is the classic full fan; 3 is the tight current-price view."
        >
          <ChoiceRow
            options={[
              { id: '3', label: '3 levels' },
              { id: '5', label: '5 levels' },
              { id: '8', label: '8 levels' },
            ]}
            value={String(bookDepthLevels)}
            onChange={(id) => setBookDepthLevels(Number(id))}
          />
        </PrefSection>

        <PrefSection
          label="Trade panel scroll"
          hint="How the side trade panel scrolls when it's taller than the screen. Pinned ticket keeps the order ticket fixed and scrolls only the order book (classic — BUY / Place Order always in view). Scroll whole panel scrolls everything together, so nothing is trapped on a short laptop screen."
        >
          <ChoiceRow
            options={[
              { id: 'pinned', label: 'Pinned ticket' },
              { id: 'whole', label: 'Scroll whole panel' },
            ]}
            value={pinnedOrderTicket ? 'pinned' : 'whole'}
            onChange={(id) => setPinnedOrderTicket(id === 'pinned')}
          />
        </PrefSection>

        <PrefSection
          label="Default dashboard sort"
          hint="Initial sort applied when the Value Dashboard mounts. Header clicks override for the session and don't change this default — set the sort you want pinned across reloads here. 'Unsorted' leaves games in upstream order (the prior behavior)."
        >
          <ChoiceRow
            options={DASHBOARD_SORT_KEY_CHOICES}
            value={defaultDashboardSort.key ?? 'unsorted'}
            onChange={(id) => {
              const key = id === 'unsorted' ? null : (id as DashboardSortKey);
              setDefaultDashboardSort({ key, dir: defaultDashboardSort.dir });
            }}
          />
          {defaultDashboardSort.key !== null && (
            <div className="mt-2">
              <ChoiceRow
                options={DASHBOARD_SORT_DIR_CHOICES}
                value={defaultDashboardSort.dir}
                onChange={(id) =>
                  setDefaultDashboardSort({
                    key: defaultDashboardSort.key,
                    dir: id as DashboardSortDir,
                  })
                }
              />
            </div>
          )}
        </PrefSection>

        <PrefSection
          label="Default positions page sort"
          hint="Initial Open Orders sort on the Positions page. Queue Asc puts front-of-line orders first; Queue Desc puts deeper queued orders first."
        >
          <ChoiceRow
            options={POSITIONS_ORDER_SORT_KEY_CHOICES}
            value={defaultPositionsOrderSort.key}
            onChange={(id) =>
              setDefaultPositionsOrderSort({
                key: id as PositionsOrderSortKey,
                dir: defaultPositionsOrderSort.dir,
              })
            }
          />
          {defaultPositionsOrderSort.key !== 'default' && (
            <div className="mt-2">
              <ChoiceRow
                options={POSITIONS_ORDER_SORT_DIR_CHOICES}
                value={defaultPositionsOrderSort.dir}
                onChange={(id) =>
                  setDefaultPositionsOrderSort({
                    key: defaultPositionsOrderSort.key,
                    dir: id as PositionsOrderSortDir,
                  })
                }
              />
            </div>
          )}
        </PrefSection>

        <PrefSection
          label="Default expiry"
          hint={
            EXPIRY_MODE_CHOICES.find((c) => c.id === defaultExpiryMode)?.hint ?? ''
          }
        >
          <ChoiceRow
            options={EXPIRY_MODE_CHOICES}
            value={defaultExpiryMode}
            onChange={(id) => setDefaultExpiryMode(id as DefaultExpiryMode)}
          />
        </PrefSection>

        <PrefSection
          label="Cancel guard"
          hint="Controls direct order-cancel buttons. Require confirm keeps the extra safety click; one click cancels immediately."
        >
          <ChoiceRow
            options={[
              { id: 'confirm', label: 'Require confirm' },
              { id: 'instant', label: 'One click' },
            ]}
            value={confirmOrderCancel ? 'confirm' : 'instant'}
            onChange={(id) => setConfirmOrderCancel(id === 'confirm')}
          />
        </PrefSection>

        <PrefSection
          label="Default contracts"
          hint="Auto-fills the Qty input the first time you pick a price (orderbook click, pill click, etc.). Doesn't pre-populate on mount and never overwrites a value you've already typed. Also accessible via the '+ N' button next to the Qty label."
        >
          <input
            type="number"
            min={1}
            step={1}
            value={defaultContracts}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (Number.isFinite(n) && n >= 1) setDefaultContracts(n);
            }}
            className="border-border bg-input text-foreground focus:border-primary w-24 rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none"
          />
        </PrefSection>
      </div>
    </div>
  );
}

function PrefSection({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
        {label}
      </label>
      {children}
      {hint && (
        <p className="text-muted-foreground mt-1.5 text-xs leading-snug">{hint}</p>
      )}
    </div>
  );
}

function ChoiceRow<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            value === opt.id
              ? 'bg-blue-500/20 text-blue-600 ring-1 ring-blue-500/40 dark:text-blue-400'
              : 'bg-secondary text-muted-foreground hover:text-foreground'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
